import React, { useEffect, useState } from 'react';

interface SuccessDialogProps {
  title: string;
  message: string;
  buttonText: string;
  onClose: () => void;
  orderId?: string;
  amount?: number;
}

export const SuccessDialog: React.FC<SuccessDialogProps> = ({
  title,
  message,
  buttonText,
  onClose,
  orderId,
  amount
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    // Trigger animation
    setTimeout(() => setIsVisible(true), 10);
    setTimeout(() => setShowConfetti(true), 300);
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
        padding: '20px',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Confetti Effect */}
      {showConfetti && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          overflow: 'hidden'
        }}>
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: '-10px',
                left: `${Math.random() * 100}%`,
                width: '10px',
                height: '10px',
                backgroundColor: ['#4CAF50', '#B8860B', '#FFD700', '#FF6B6B', '#4ECDC4'][i % 5],
                opacity: 0.8,
                borderRadius: '50%',
                animation: `confetti-fall ${2 + Math.random() * 2}s linear forwards`,
                animationDelay: `${Math.random() * 0.5}s`
              }}
            />
          ))}
        </div>
      )}

      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          maxWidth: '450px',
          width: '100%',
          padding: '32px',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          transform: isVisible ? 'scale(1)' : 'scale(0.9)',
          transition: 'transform 0.3s ease-out'
        }}
      >
        {/* Success Icon with Animation */}
        <div
          style={{
            width: '100px',
            height: '100px',
            backgroundColor: '#E8F5E9',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            animation: 'success-pulse 0.6s ease-out',
            position: 'relative'
          }}
        >
          <svg
            width="60"
            height="60"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#4CAF50"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path
              d="M20 6L9 17l-5-5"
              style={{
                strokeDasharray: 100,
                strokeDashoffset: isVisible ? 0 : 100,
                animation: 'check-draw 0.5s ease-out 0.3s forwards'
              }}
            />
          </svg>

          {/* Checkmark circle background */}
          <div style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '4px solid #4CAF50',
            animation: 'circle-draw 0.5s ease-out forwards'
          }} />
        </div>

        {/* Title */}
        <h2
          style={{
            fontSize: '26px',
            fontWeight: 'bold',
            color: '#1A1A1A',
            margin: '0 0 12px 0',
            fontFamily: 'Montserrat, sans-serif'
          }}
        >
          {title}
        </h2>

        {/* Message */}
        <p
          style={{
            fontSize: '16px',
            color: '#6B7280',
            margin: '0 0 24px 0',
            lineHeight: '1.6',
            fontFamily: 'Montserrat, sans-serif'
          }}
        >
          {message}
        </p>

        {/* Order Details Card */}
        {(orderId || amount) && (
          <div style={{
            backgroundColor: '#F9FAFB',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            border: '1px solid #E5E7EB'
          }}>
            {orderId && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: amount ? '12px' : '0'
              }}>
                <span style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  fontFamily: 'Montserrat, sans-serif'
                }}>
                  Order ID
                </span>
                <span style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#1A1A1A',
                  fontFamily: 'monospace',
                  backgroundColor: '#FFFFFF',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  border: '1px solid #E5E7EB'
                }}>
                  {orderId}
                </span>
              </div>
            )}

            {amount && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: orderId ? '12px' : '0',
                borderTop: orderId ? '1px solid #E5E7EB' : 'none'
              }}>
                <span style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  fontFamily: 'Montserrat, sans-serif'
                }}>
                  Amount Paid
                </span>
                <span style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#10B981',
                  fontFamily: 'Montserrat, sans-serif'
                }}>
                  €{amount.toFixed(2)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Info Box */}
        <div style={{
          backgroundColor: '#FEF3C7',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          textAlign: 'left'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#F59E0B" style={{ flexShrink: 0, marginTop: '2px' }}>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" stroke="#FEF3C7" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span style={{
            fontSize: '13px',
            color: '#92400E',
            lineHeight: '1.5',
            fontFamily: 'Montserrat, sans-serif'
          }}>
            A confirmation email has been sent to your registered email address.
          </span>
        </div>

        {/* Button */}
        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: '#B8860B',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s, transform 0.1s',
            fontFamily: 'Montserrat, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#9A7209';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#B8860B';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {buttonText}
        </button>
      </div>

      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes success-pulse {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes check-draw {
          from {
            stroke-dashoffset: 100;
          }
          to {
            stroke-dashoffset: 0;
          }
        }

        @keyframes circle-draw {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};
